import { useCallback, useEffect, useState } from "react";
import { DashboardEntity } from "../../../entities/company/Dashboard/DashboardEntity";
import { DashboardFilterEntity } from "../../../entities/company/Dashboard/DashboardFilterEntity";
import { FilterRequest } from "../../../entities/Filter/FilterRequest";
import DashboardService from "../../../services/DashboardService";
import Loading from "../../shared/components/Loading";
import { TranslationService } from "../../../services/TranslationService";
import HighchartChart from "./HighchartChart";
import ErrorCatcher from "../../shared/ErrorCatcher";
import DashboardSalesDSO from "../../client/components/DashboardSalesDSO";

const DebtAgingChart = ({
  dashboard,
  dashboardFilters,
  filterCallback
}: {
  dashboard: DashboardEntity;
  dashboardFilters: FilterRequest[];
  filterCallback: (dashboardFilterEntity: DashboardFilterEntity) => void;
}) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [response, setResponse] = useState<Highcharts.Options>();
  const [valuesEnabled, setValuesEnabled] = useState(false);
  const [dashboardCalculate, setDashboardCalculate] = useState(dashboard);

  const requestData = async () => {
    setLoading(true);
    const result = await DashboardService.calculate(
      dashboard.DashboardID,
      dashboardFilters
    );
    setLoading(false);
    if (result instanceof Error) {
      setError(true);
      return;
    }
    setError(false);
    const chart = result as {
      dashboard: DashboardEntity;
      optionextend: Highcharts.Options;
    };
    if (chart.optionextend?.colors === null) {
      chart.optionextend.colors = [
        "#7cb5ec",
        "#434348",
        "#90ed7d",
        "#f7a35c",
        "#8085e9",
        "#f15c80",
        "#e4d354",
        "#2b908f",
        "#f45b5b",
        "#91e8e1"
      ];
    }
    chart.optionextend.exporting?.buttons?.contextButton?.menuItems?.push(
      "separator"
    );
    const buttons =
      chart.optionextend.exporting?.buttons?.contextButton?.menuItems;
    const button = {
      text: TranslationService.translate.ShowHideValues,
      onclick: () => setValuesEnabled((prev) => !prev)
    };
    (buttons as any).push(button);
    chart.optionextend!.plotOptions!.column!.pointWidth = 36;
    chart.optionextend.chart!.height = 250;

    if (chart.optionextend.chart !== undefined) {
      chart.optionextend!.plotOptions!.column!.borderRadius = "4px";
    }
    setDashboardCalculate({ ...chart.dashboard });
    setResponse({ ...chart.optionextend });
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const requestDataCallback = useCallback(requestData, [
    dashboard.DashboardID,
    dashboardFilters
  ]);

  useEffect(() => {
    requestDataCallback();
  }, [requestDataCallback]);

  if (loading) {
    return <Loading height={556} />;
  }

  if (error || !response) {
    return <></>;
  }

  const dashboarDonut = { ...dashboardCalculate };
  dashboarDonut.Type = 0;
  //const responseDonut = {...response};
  if (response.title !== undefined) {
    response.title.text = "";
  }
  if (response.exporting !== undefined) {
    //response.exporting.enabled = false;
  }
  const responseDonut = JSON.parse(JSON.stringify(response));

  if (responseDonut.chart !== undefined) {
    responseDonut.chart.type = "pie";
    responseDonut.plotOptions.pie.innerSize = "66%";
    responseDonut.plotOptions.pie.dataLabels.enabled = false;
    responseDonut.plotOptions.pie.showInLegend = true;
    responseDonut.exporting.enabled = false;
    if (responseDonut.chart !== undefined) {
      responseDonut!.plotOptions!.pie!.borderRadius = "0%";
    }

    const series = responseDonut.series[0].data;
    const seriesDonut: any[][] = [
      [
        TranslationService.translate.NoDue,
        0,
        "-33",
        "0",
        "#0076B6",
        "",
        2,
        "601001628.09",
        "0"
      ],
      [
        TranslationService.translate.Due,
        0,
        "-33",
        "1",
        "#EF4E38",
        "",
        2,
        "601001628.09",
        "0"
      ]
    ];
    for (let i = 0; i < series.length; i++) {
      if (series[i][4] === "#EF4E38")
        seriesDonut[1][1] = seriesDonut[1][1] + series[i][1];
      else seriesDonut[0][1] = seriesDonut[0][1] + series[i][1];
    }
    responseDonut.series[0].data = seriesDonut;
    responseDonut.colors = ["#0076B6", "#EF4E38"];
  }

  if (
    responseDonut.series[0].data[0][1] === 0 &&
    responseDonut.series[0].data[1][1] === 0
  ) {
    return <></>;
  }
  if (!responseDonut.chart.events) {
    responseDonut.chart.events = {};
  }

  responseDonut.chart.events.render = function () {
    const chart = this;
    const centerX = chart.plotWidth / 2 + chart.plotLeft;
    const centerY = chart.plotHeight / 2 + chart.plotTop;

    if (chart.customText) {
      chart.customText.destroy();
    }

    const noDue = responseDonut.series[0].data[0][1];
    const overdue = responseDonut.series[0].data[1][1];
    const total = noDue + overdue;
    const percent = total === 0 ? 0 : Math.round((overdue / total) * 100);

    chart.customText = chart.renderer
      .text(
        `${percent}%`, // Texto en el centro
        centerX,
        centerY
      )
      .attr({
        align: "center"
      })
      .css({
        color: "#ef4e38",
        fontSize: "30px",
        fontFamily: "Lato",
        fontWeight: "bold"
      })
      .add();
    chart.customText.attr({
      x: centerX - chart.customText.getBBox().width / 100,
      y: centerY + chart.customText.getBBox().height / 4
    });
  };
  return (
    <div className="card">
      <div className="card-body">
        <ErrorCatcher onError={() => setError(true)}>
          <div className="mb-4 p-0">
            <DashboardSalesDSO
              dashboardFilters={dashboardFilters}
              noDue={responseDonut.series[0].data[0][1]}
              overdue={responseDonut.series[0].data[1][1]}
              totalAmount={
                responseDonut.series[0].data[0][1] +
                responseDonut.series[0].data[1][1]
              }
            />
          </div>
          <div className="dashboard__sales row">
            <div className="col-4">
              <div className="m-5 mt-0 mb-0">
                <HighchartChart
                  dashboard={dashboarDonut}
                  options={responseDonut}
                  dashboardFilters={dashboardFilters}
                  filterCallback={filterCallback}
                  valuesEnabled={valuesEnabled}
                />
              </div>
            </div>
            <div className="col-8">
              <HighchartChart
                dashboard={dashboardCalculate}
                options={response}
                dashboardFilters={dashboardFilters}
                filterCallback={filterCallback}
                valuesEnabled={valuesEnabled}
              />
            </div>
          </div>
        </ErrorCatcher>
      </div>
    </div>
  );
};
export default DebtAgingChart;
